import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import {
  HeartPulse, Search, Loader2, ChevronLeft, ChevronRight, MapPin, Phone, Globe,
  ExternalLink, BedDouble, Star, RefreshCw, Building2, X, Plus, CheckCircle2,
  Download, Calendar, Users, FileText, Activity, Bookmark, MoreVertical, Send, Sparkles, Trash2, CalendarClock,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RATING_STYLES = {
  'Outstanding': 'bg-emerald-100 text-emerald-700 border-emerald-300',
  'Good': 'bg-sky-100 text-sky-700 border-sky-300',
  'Requires improvement': 'bg-amber-100 text-amber-700 border-amber-300',
  'Inadequate': 'bg-rose-100 text-rose-700 border-rose-300',
};

const PROVIDER_TYPE_LABELS = {
  care_home: 'Care Home', nursing_home: 'Nursing Home',
  homecare: 'Homecare', supported_living: 'Supported Living',
};

const ALL = '__all__';

const RatingBadge = ({ rating }) => {
  if (!rating) return <span className="text-gray-400 text-xs">-</span>;
  const cls = RATING_STYLES[rating] || 'bg-gray-100 text-gray-600 border-gray-300';
  return (
    <Badge variant="outline" className={`${cls} text-xs font-medium`} data-testid="cqc-rating-badge">
      <Star className="w-3 h-3 mr-1" />{rating}
    </Badge>
  );
};

const CareDirectory = () => {
  const { user } = useAuth();
  const isAdmin = user?.is_admin || user?.role === 'owner' || user?.role === 'admin';

  const [meta, setMeta] = useState(null);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(25);
  const [loading, setLoading] = useState(false);

  const [search, setSearch] = useState('');
  const [region, setRegion] = useState(ALL);
  const [rating, setRating] = useState(ALL);
  const [providerType, setProviderType] = useState(ALL);
  const [capacityBand, setCapacityBand] = useState(ALL);

  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [adding, setAdding] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [exportInfo, setExportInfo] = useState({ allowed: false, plan_tier: 'free' });
  const [exporting, setExporting] = useState(false);
  const [segments, setSegments] = useState([]);
  const [saveOpen, setSaveOpen] = useState(false);
  const [segName, setSegName] = useState('');
  const [savingSeg, setSavingSeg] = useState(false);
  const [scheduleSeg, setScheduleSeg] = useState(null);
  const [schedEnabled, setSchedEnabled] = useState(false);
  const [schedFreq, setSchedFreq] = useState('weekly');
  const [schedEmail, setSchedEmail] = useState('');
  const [buildingSeg, setBuildingSeg] = useState(null);
  const [findingContacts, setFindingContacts] = useState(false);

  const loadMeta = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/cqc/meta`);
      setMeta(data);
    } catch (e) { /* noop */ }
  }, []);

  const loadRows = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, page_size: pageSize };
      if (search) params.search = search;
      if (region !== ALL) params.region = region;
      if (rating !== ALL) params.rating = rating;
      if (providerType !== ALL) params.provider_type = providerType;
      if (capacityBand !== ALL) params.capacity_band = capacityBand;
      const { data } = await axios.get(`${API}/cqc/locations`, { params });
      setRows(data.data || []);
      setTotal(data.total || 0);
    } catch (e) {
      toast.error('Could not load the care directory');
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, search, region, rating, providerType, capacityBand]);

  useEffect(() => { loadMeta(); }, [loadMeta]);
  useEffect(() => { loadRows(); }, [loadRows]);

  useEffect(() => {
    axios.get(`${API}/cqc/export-eligibility`)
      .then(({ data }) => setExportInfo(data))
      .catch(() => {});
  }, []);

  const loadSegments = useCallback(() => {
    axios.get(`${API}/cqc/segments`).then(({ data }) => setSegments(data.segments || [])).catch(() => {});
  }, []);
  useEffect(() => { loadSegments(); }, [loadSegments]);

  const saveSegment = async () => {
    if (!segName.trim()) { toast.error('Name your segment'); return; }
    setSavingSeg(true);
    try {
      await axios.post(`${API}/cqc/segments`, { name: segName.trim(), filters: currentFilterParams() });
      toast.success('Segment saved');
      setSaveOpen(false); setSegName('');
      loadSegments();
    } catch (e) { toast.error('Could not save segment'); }
    finally { setSavingSeg(false); }
  };

  const applySegment = (seg) => {
    const f = seg.filters || {};
    setSearch(f.search || '');
    setRegion(f.region || ALL);
    setRating(f.rating || ALL);
    setProviderType(f.provider_type || ALL);
    setCapacityBand(f.capacity_band || ALL);
    setPage(1);
    toast.success(`Applied “${seg.name}”`);
  };

  const deleteSegment = async (seg) => {
    try { await axios.delete(`${API}/cqc/segments/${seg.id}`); loadSegments(); toast.success('Segment deleted'); }
    catch (e) { toast.error('Could not delete'); }
  };

  const buildCampaign = async (seg) => {
    setBuildingSeg(seg.id);
    try {
      const { data } = await axios.post(`${API}/cqc/segments/${seg.id}/to-campaign`, {}, { params: { limit: 25 } });
      toast.success(`Draft campaign ready - ${data.companies_added} providers added, ${data.contacts_found} decision-makers found via Hunter`);
      window.location.href = `/campaigns`;
    } catch (e) { toast.error('Could not build campaign'); }
    finally { setBuildingSeg(null); }
  };

  const openSchedule = (seg) => {
    const s = seg.schedule || {};
    setScheduleSeg(seg);
    setSchedEnabled(!!s.enabled);
    setSchedFreq(s.frequency || 'weekly');
    setSchedEmail(s.email || '');
  };

  const saveSchedule = async () => {
    try {
      await axios.put(`${API}/cqc/segments/${scheduleSeg.id}`, {
        schedule: { enabled: schedEnabled, frequency: schedFreq, email: schedEmail.trim() || null },
      });
      toast.success(schedEnabled ? `Scheduled ${schedFreq} export to ${schedEmail}` : 'Schedule turned off');
      setScheduleSeg(null);
      loadSegments();
    } catch (e) { toast.error('Could not save schedule'); }
  };

  const findContacts = async () => {
    if (!selected) return;
    setFindingContacts(true);
    try {
      const { data } = await axios.post(`${API}/cqc/locations/${selected.locationId}/find-contacts`);
      if (data.hunter_error === 'rate_limited') {
        toast.error('Hunter search limit reached for this billing period');
      } else if (data.reason === 'no_website') {
        toast.error('No website on file for this provider');
      } else if (data.found === 0) {
        toast('No decision-maker emails found via Hunter');
      } else {
        toast.success(`Found ${data.found} contact${data.found === 1 ? '' : 's'} via Hunter`);
      }
      const { data: fresh } = await axios.get(`${API}/cqc/locations/${selected.locationId}`);
      setDetail(fresh);
    } catch (e) { toast.error('Hunter lookup failed'); }
    finally { setFindingContacts(false); }
  };

  const currentFilterParams = () => {
    const p = {};
    if (search) p.search = search;
    if (region !== ALL) p.region = region;
    if (rating !== ALL) p.rating = rating;
    if (providerType !== ALL) p.provider_type = providerType;
    if (capacityBand !== ALL) p.capacity_band = capacityBand;
    return p;
  };

  const exportXlsx = async () => {
    if (!exportInfo.allowed) {
      toast.error("Exporting the directory isn't on your plan - upgrade to a paid tier to download segments.");
      return;
    }
    setExporting(true);
    try {
      const res = await axios.get(`${API}/cqc/locations/export`, {
        params: currentFilterParams(), responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'cqc-care-directory.xlsx';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Export downloaded');
    } catch (e) {
      toast.error(e?.response?.status === 402 ? 'Upgrade your plan to export the directory' : 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  // Poll sync status while a seed is running.
  useEffect(() => {
    if (meta?.sync?.status !== 'running') return;
    const t = setInterval(loadMeta, 5000);
    return () => clearInterval(t);
  }, [meta?.sync?.status, loadMeta]);

  const openDetail = async (loc) => {
    setSelected(loc);
    setDetail(null);
    setDetailLoading(true);
    try {
      const { data } = await axios.get(`${API}/cqc/locations/${loc.locationId}`);
      setDetail(data);
    } catch (e) {
      toast.error('Could not load location details');
    } finally {
      setDetailLoading(false);
    }
  };

  const addToCrm = async () => {
    if (!selected) return;
    setAdding(true);
    try {
      const { data } = await axios.post(`${API}/cqc/locations/${selected.locationId}/add-to-crm`);
      toast.success(data.created ? 'Added to your CRM as a care provider' : 'Already in your CRM');
      // Re-fetch so the "In your CRM" drill-down (contacts/activities) shows immediately.
      try {
        const { data: fresh } = await axios.get(`${API}/cqc/locations/${selected.locationId}`);
        setDetail(fresh);
      } catch {
        setDetail((d) => ({ ...d, crm_company_id: data.company_id }));
      }
    } catch (e) {
      toast.error('Could not add to CRM');
    } finally {
      setAdding(false);
    }
  };

  const startSeed = async () => {
    setSeeding(true);
    try {
      await axios.post(`${API}/cqc/sync/seed`);
      toast.success('CQC sync started - the directory will fill in over the next few hours');
      setTimeout(loadMeta, 1500);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not start sync');
    } finally {
      setSeeding(false);
    }
  };

  const downloadReport = async (linkId) => {
    if (!linkId || !selected) return;
    try {
      const res = await axios.get(`${API}/cqc/locations/${selected.locationId}/report/${linkId}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      window.open(url, '_blank');
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch (e) {
      toast.error('Could not load the CQC report PDF');
    }
  };

  const resetFilters = () => {
    setSearch(''); setRegion(ALL); setRating(ALL); setProviderType(ALL); setCapacityBand(ALL); setPage(1);
  };

  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const sync = meta?.sync || {};
  const counts = meta?.counts || {};
  const anyFilter = search || region !== ALL || rating !== ALL || providerType !== ALL || capacityBand !== ALL;

  return (
    <div className="space-y-6" data-testid="care-directory-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-[#00aeef]/10 flex items-center justify-center">
            <HeartPulse className="w-6 h-6 text-[#00aeef]" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Care Directory</h1>
            <p className="text-sm text-gray-500">
              UK CQC-registered care providers & locations
              {counts.locations ? ` · ${counts.locations.toLocaleString()} locations` : ''}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {exportInfo.allowed && (
            <Button variant="outline" size="sm" onClick={exportXlsx} disabled={exporting} data-testid="cqc-export-btn"
              title="Download this filtered segment as XLSX">
              {exporting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Download className="w-4 h-4 mr-1" />}
              Export XLSX
            </Button>
          )}
          {isAdmin && (sync.status === 'running' ? (
            <Badge variant="outline" className="bg-sky-50 text-sky-700 border-sky-200" data-testid="cqc-sync-status">
              <Loader2 className="w-3 h-3 mr-1 animate-spin" />
              Syncing… {(counts.enriched || 0).toLocaleString()}/{(counts.locations || 0).toLocaleString()}
            </Badge>
          ) : (
            <Button variant="outline" size="sm" onClick={startSeed} disabled={seeding} data-testid="cqc-seed-btn">
              {seeding ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1" />}
              {sync.last_full_seed_at ? 'Re-sync CQC data' : 'Sync CQC data'}
            </Button>
          ))}
        </div>
      </div>

      {/* Empty / first-run hint */}
      {counts.locations === 0 && (
        <Card><CardContent className="py-10 text-center">
          <Building2 className="w-10 h-10 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-600 font-medium">The care directory is empty</p>
          <p className="text-sm text-gray-400 mt-1">
            {isAdmin ? 'Click “Sync CQC data” to pull the live UK care directory from CQC.' : 'Ask an admin to sync the CQC directory.'}
          </p>
        </CardContent></Card>
      )}

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-3">
            <div className="lg:col-span-2 relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <Input
                data-testid="cqc-search-input"
                placeholder="Search by name…"
                className="pl-9"
                value={search}
                onChange={(e) => { setPage(1); setSearch(e.target.value); }}
              />
            </div>
            <Select value={region} onValueChange={(v) => { setPage(1); setRegion(v); }}>
              <SelectTrigger data-testid="cqc-region-filter"><SelectValue placeholder="Region" /></SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>All regions</SelectItem>
                {(meta?.regions || []).map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={rating} onValueChange={(v) => { setPage(1); setRating(v); }}>
              <SelectTrigger data-testid="cqc-rating-filter"><SelectValue placeholder="Rating" /></SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>All ratings</SelectItem>
                {(meta?.ratings || []).map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={providerType} onValueChange={(v) => { setPage(1); setProviderType(v); }}>
              <SelectTrigger data-testid="cqc-type-filter"><SelectValue placeholder="Service type" /></SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>All service types</SelectItem>
                {(meta?.provider_types || []).map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={capacityBand} onValueChange={(v) => { setPage(1); setCapacityBand(v); }}>
              <SelectTrigger data-testid="cqc-capacity-filter"><SelectValue placeholder="Capacity" /></SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>Any capacity</SelectItem>
                {(meta?.capacity_bands || []).map((b) => <SelectItem key={b.value} value={b.value}>{b.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {anyFilter && (
            <div className="mt-3 flex items-center justify-between">
              <span className="text-xs text-gray-500">{total.toLocaleString()} matching locations</span>
              <Button variant="ghost" size="sm" onClick={resetFilters} data-testid="cqc-reset-filters">
                <X className="w-3 h-3 mr-1" /> Clear filters
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Saved segments */}
      <div className="flex flex-wrap items-center gap-2" data-testid="cqc-segments-bar">
        <Button variant="outline" size="sm" onClick={() => setSaveOpen(true)} data-testid="save-segment-btn">
          <Bookmark className="w-4 h-4 mr-1" /> Save this view
        </Button>
        {segments.map((seg) => (
          <div key={seg.id} className="flex items-center rounded-full border border-gray-200 bg-white pl-3 pr-1 py-1 text-xs" data-testid={`segment-chip-${seg.id}`}>
            <button onClick={() => applySegment(seg)} className="font-medium text-slate-700 hover:text-[#00aeef]">
              {seg.name} <span className="text-gray-400">({seg.match_count})</span>
            </button>
            {seg.schedule?.enabled && <CalendarClock className="w-3 h-3 ml-1.5 text-emerald-500" title="Scheduled export on" />}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="ml-1 p-1 text-gray-400 hover:text-gray-700" data-testid={`segment-menu-${seg.id}`}><MoreVertical className="w-3.5 h-3.5" /></button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => buildCampaign(seg)} data-testid={`segment-build-${seg.id}`} disabled={buildingSeg === seg.id}>
                  {buildingSeg === seg.id ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />} Build campaign (Hunter)
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => openSchedule(seg)} data-testid={`segment-schedule-${seg.id}`}>
                  <CalendarClock className="w-4 h-4 mr-2" /> Schedule export…
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => deleteSegment(seg)} className="text-rose-600" data-testid={`segment-delete-${seg.id}`}>
                  <Trash2 className="w-4 h-4 mr-2" /> Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        ))}
      </div>

      {/* Results */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="py-16 text-center text-gray-400"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
          ) : rows.length === 0 ? (
            <div className="py-16 text-center text-gray-400">No locations match your filters.</div>
          ) : (
            <table className="w-full stack-mobile" data-testid="cqc-locations-table">
              <thead className="bg-gray-50 border-b border-gray-100">
                <tr className="text-left text-xs font-semibold text-gray-500 uppercase">
                  <th className="px-4 py-3">Name</th>
                  <th className="px-4 py-3">Region</th>
                  <th className="px-4 py-3">Type</th>
                  <th className="px-4 py-3">Beds</th>
                  <th className="px-4 py-3">Rating</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {rows.map((loc) => (
                  <tr
                    key={loc.locationId}
                    className="hover:bg-sky-50/50 cursor-pointer"
                    onClick={() => openDetail(loc)}
                    data-testid={`cqc-row-${loc.locationId}`}
                  >
                    <td className="px-4 py-3" data-label="Name">
                      <div className="font-medium text-slate-900">{loc.name}</div>
                      <div className="text-xs text-gray-400">{loc.postalCode}</div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600" data-label="Region">{loc.region || '-'}</td>
                    <td className="px-4 py-3 text-sm text-gray-600" data-label="Type">
                      {PROVIDER_TYPE_LABELS[loc.providerType] || (loc.careHome ? 'Care Home' : '-')}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600" data-label="Beds">{loc.numberOfBeds ?? '-'}</td>
                    <td className="px-4 py-3" data-label="Rating"><RatingBadge rating={loc.overallRating} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      {total > pageSize && (
        <div className="flex items-center justify-between">
          <span className="text-sm text-gray-500">Page {page} of {totalPages.toLocaleString()}</span>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} data-testid="cqc-prev-page">
              <ChevronLeft className="w-4 h-4" /> Prev
            </Button>
            <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)} data-testid="cqc-next-page">
              Next <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}

      {/* Detail dialog */}
      <Dialog open={!!selected} onOpenChange={(o) => { if (!o) { setSelected(null); setDetail(null); } }}>
        <DialogContent className="max-w-lg" data-testid="cqc-detail-dialog">
          <DialogHeader>
            <DialogTitle className="text-slate-900">{selected?.name}</DialogTitle>
            <DialogDescription>CQC location {selected?.locationId}</DialogDescription>
          </DialogHeader>
          {detailLoading ? (
            <div className="py-10 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-gray-400" /></div>
          ) : detail ? (
            <div className="space-y-4">
              <div className="flex flex-wrap items-center gap-2">
                <RatingBadge rating={detail.location.overallRating} />
                {detail.location.providerType && (
                  <Badge variant="outline" className="text-xs">{PROVIDER_TYPE_LABELS[detail.location.providerType]}</Badge>
                )}
                {detail.location.numberOfBeds != null && (
                  <Badge variant="outline" className="text-xs"><BedDouble className="w-3 h-3 mr-1" />{detail.location.numberOfBeds} beds</Badge>
                )}
              </div>

              {/* Inspection key-question (domain) ratings */}
              {(detail.location.keyQuestionRatings || []).length > 0 && (
                <div className="grid grid-cols-5 gap-1.5" data-testid="cqc-key-ratings">
                  {detail.location.keyQuestionRatings.map((kq) => (
                    <div key={kq.name} className="text-center rounded-lg border border-gray-100 py-1.5 px-1">
                      <div className="text-[10px] text-gray-400 uppercase tracking-wide truncate">{kq.name}</div>
                      <div className="text-xs font-semibold text-slate-800 leading-tight mt-0.5">{kq.rating || '-'}</div>
                    </div>
                  ))}
                </div>
              )}

              {/* Ratings history timeline + report PDFs */}
              {(() => {
                const loc = detail.location;
                const timeline = [];
                if (loc.overallRating) {
                  timeline.push({ date: loc.ratingDate || loc.lastInspectionDate, rating: loc.overallRating, linkId: (loc.reports || [])[0]?.linkId, current: true });
                }
                (loc.historicRatings || []).forEach((h) => timeline.push({ date: h.reportDate, rating: h.rating, linkId: h.reportLinkId }));
                if (timeline.length === 0 && !loc.cqcLocationUrl) return null;
                return (
                  <div data-testid="cqc-ratings-history">
                    {timeline.length > 0 && (
                      <>
                        <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400 mb-1.5">Ratings history</p>
                        <div className="space-y-1.5">
                          {timeline.map((t, i) => (
                            <div key={i} className="flex items-center gap-2 text-xs">
                              <span className="text-gray-400 w-20 shrink-0">{(t.date || '').slice(0, 10) || '-'}</span>
                              <RatingBadge rating={t.rating} />
                              {t.current && <span className="text-[10px] text-emerald-600 font-semibold">current</span>}
                              {t.linkId && (
                                <button onClick={() => downloadReport(t.linkId)} className="ml-auto inline-flex items-center gap-1 text-[#00aeef] hover:underline" data-testid={`cqc-report-pdf-${i}`}>
                                  <FileText className="w-3 h-3" /> PDF
                                </button>
                              )}
                            </div>
                          ))}
                        </div>
                      </>
                    )}
                    {loc.cqcLocationUrl && (
                      <a href={loc.cqcLocationUrl} target="_blank" rel="noreferrer"
                         className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-[#00aeef] mt-2" data-testid="cqc-report-link">
                        <ExternalLink className="w-3 h-3" /> View full profile on cqc.org.uk
                      </a>
                    )}
                  </div>
                );
              })()}
              <div className="text-sm space-y-2 text-gray-700">
                <div className="flex items-start gap-2">
                  <MapPin className="w-4 h-4 text-gray-400 mt-0.5 shrink-0" />
                  <span>{[detail.location.addressLine1, detail.location.townCity, detail.location.postalCode].filter(Boolean).join(', ') || detail.location.region || '-'}</span>
                </div>
                {detail.location.phone && (
                  <div className="flex items-center gap-2"><Phone className="w-4 h-4 text-gray-400" /><span>{detail.location.phone}</span></div>
                )}
                {detail.location.website && (
                  <div className="flex items-center gap-2">
                    <Globe className="w-4 h-4 text-gray-400" />
                    <a href={detail.location.website.startsWith('http') ? detail.location.website : `https://${detail.location.website}`}
                       target="_blank" rel="noreferrer" className="text-[#00aeef] hover:underline inline-flex items-center gap-1">
                      {detail.location.website} <ExternalLink className="w-3 h-3" />
                    </a>
                  </div>
                )}
                {detail.provider?.name && (
                  <div className="flex items-center gap-2"><Building2 className="w-4 h-4 text-gray-400" /><span>Provider: <strong>{detail.provider.name}</strong></span></div>
                )}
              </div>
              {(detail.location.serviceTypes || []).length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {detail.location.serviceTypes.map((s) => <Badge key={s} variant="outline" className="text-xs bg-gray-50">{s}</Badge>)}
                </div>
              )}
              <div className="pt-2 border-t border-gray-100">
                {detail.crm && (
                  <div className="mb-3 rounded-lg bg-sky-50/60 border border-sky-100 p-3" data-testid="cqc-crm-drilldown">
                    <div className="flex items-center gap-1.5 text-xs font-semibold text-slate-700 mb-2">
                      <Users className="w-3.5 h-3.5 text-[#00aeef]" /> In your CRM - {detail.crm.contact_count} contact{detail.crm.contact_count === 1 ? '' : 's'}
                    </div>
                    {(detail.crm.contacts || []).slice(0, 5).map((ct) => (
                      <div key={ct.id} className="flex items-center justify-between text-xs text-gray-600 py-0.5">
                        <span>{ct.full_name}{ct.title ? ` · ${ct.title}` : ''}</span>
                        <span className="text-gray-400">{ct.email}</span>
                      </div>
                    ))}
                    {(detail.crm.activities || []).length > 0 && (
                      <div className="mt-2 pt-2 border-t border-sky-100">
                        {detail.crm.activities.slice(0, 3).map((a) => (
                          <div key={a.id} className="flex items-center gap-1.5 text-[11px] text-gray-500 py-0.5">
                            <Activity className="w-3 h-3 text-gray-400 shrink-0" />
                            <span className="truncate">{a.subject || a.type}</span>
                            <span className="text-gray-300 ml-auto shrink-0">{(a.occurred_at || a.created_at || '').slice(0, 10)}</span>
                          </div>
                        ))}
                      </div>
                    )}
                    <a href={`/companies?open=${detail.crm.company_id}`} className="inline-flex items-center gap-1 text-[11px] text-[#00aeef] hover:underline mt-2" data-testid="cqc-open-company">
                      Open company record <ChevronRight className="w-3 h-3" />
                    </a>
                  </div>
                )}
                {detail.crm_company_id ? (
                  <Button variant="outline" className="w-full text-emerald-700 border-emerald-300" disabled data-testid="cqc-in-crm">
                    <CheckCircle2 className="w-4 h-4 mr-1" /> In your CRM
                  </Button>
                ) : (
                  <Button className="w-full bg-[#00aeef] hover:bg-[#0098d1]" onClick={addToCrm} disabled={adding} data-testid="cqc-add-to-crm-btn">
                    {adding ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Plus className="w-4 h-4 mr-1" />}
                    Add to my CRM
                  </Button>
                )}
                {detail.location.website && user?.role === 'owner' && (
                  <Button variant="outline" className="w-full mt-2" onClick={findContacts} disabled={findingContacts} data-testid="cqc-find-contacts-btn">
                    {findingContacts ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Users className="w-4 h-4 mr-1" />}
                    Find decision-makers (Hunter)
                  </Button>
                )}
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>

      {/* Save segment dialog */}
      <Dialog open={saveOpen} onOpenChange={setSaveOpen}>
        <DialogContent className="max-w-md" data-testid="save-segment-dialog">
          <DialogHeader>
            <DialogTitle>Save this view as a segment</DialogTitle>
            <DialogDescription>Saves your current filters so you can re-apply, build a campaign, or schedule an export.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Input placeholder="e.g. Outstanding nursing homes, North West" value={segName}
              onChange={(e) => setSegName(e.target.value)} data-testid="segment-name-input" />
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setSaveOpen(false)}>Cancel</Button>
              <Button className="bg-[#00aeef] hover:bg-[#0098d1]" onClick={saveSegment} disabled={savingSeg} data-testid="segment-save-confirm">
                {savingSeg ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Bookmark className="w-4 h-4 mr-1" />} Save segment
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Schedule export dialog */}
      <Dialog open={!!scheduleSeg} onOpenChange={(o) => { if (!o) setScheduleSeg(null); }}>
        <DialogContent className="max-w-md" data-testid="schedule-segment-dialog">
          <DialogHeader>
            <DialogTitle>Schedule export - {scheduleSeg?.name}</DialogTitle>
            <DialogDescription>Email a fresh XLSX of this segment on a recurring basis.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <Label>Enabled</Label>
              <Switch checked={schedEnabled} onCheckedChange={setSchedEnabled} data-testid="schedule-enabled-switch" />
            </div>
            <div>
              <Label className="text-xs text-gray-500">Frequency</Label>
              <Select value={schedFreq} onValueChange={setSchedFreq}>
                <SelectTrigger data-testid="schedule-frequency"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="weekly">Weekly</SelectItem>
                  <SelectItem value="monthly">Monthly</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-gray-500">Send to</Label>
              <Input type="email" placeholder="you@company.com" value={schedEmail}
                onChange={(e) => setSchedEmail(e.target.value)} data-testid="schedule-email-input" />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setScheduleSeg(null)}>Cancel</Button>
              <Button className="bg-[#00aeef] hover:bg-[#0098d1]" onClick={saveSchedule} data-testid="schedule-save-btn">Save schedule</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CareDirectory;
